import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Mail, RefreshCw, Trash2, CheckCircle, XCircle, AlertTriangle, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FilterSelect from '../components/FilterSelect';
import api from '../services/api';

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const AdminEmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, success: 0, failed: 0, no_api_key: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ status: '', email_type: '' });
  const [clearing, setClearing] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (filters.status) params.append('status', filters.status);
      if (filters.email_type) params.append('email_type', filters.email_type);
      
      const res = await api.get(`/admin/settings/email-logs?${params}`);
      setLogs(res.data.logs);
      setTotalPages(res.data.total_pages);
      setStats(res.data.stats);
    } catch (error) {
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all email logs?')) return;
    
    setClearing(true);
    try {
      await api.delete('/admin/settings/email-logs');
      toast.success('Email logs cleared');
      fetchLogs();
    } catch (error) {
      toast.error('Failed to clear logs');
    } finally {
      setClearing(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'no_api_key':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      no_api_key: 'bg-yellow-100 text-yellow-800'
    };
    const labels = {
      success: 'Sent',
      failed: 'Failed',
      no_api_key: 'No API Key'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const styles = {
      verification: 'bg-blue-100 text-blue-800',
      test: 'bg-purple-100 text-purple-800',
      tier_notification: 'bg-orange-100 text-orange-800',
      general: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[type] || 'bg-gray-100 text-gray-800'}`}>
        {type}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <Sidebar>
      <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl" style={{ backgroundColor: `${BRAND_BLUE}15` }}>
              <Mail className="w-7 h-7" style={{ color: BRAND_BLUE }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Email Logs</h1>
              <p className="text-slate-500 text-sm">Debug and monitor all email activity</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleClearLogs}
              disabled={clearing || stats.total === 0}
              className="flex items-center gap-2 px-4 py-2 text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              Clear All
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl border border-slate-200">
            <div className="text-sm text-slate-500 mb-1">Total Emails</div>
            <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-green-200">
            <div className="text-sm text-green-600 mb-1">Sent Successfully</div>
            <div className="text-2xl font-bold text-green-700">{stats.success}</div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-red-200">
            <div className="text-sm text-red-600 mb-1">Failed</div>
            <div className="text-2xl font-bold text-red-700">{stats.failed}</div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-yellow-200">
            <div className="text-sm text-yellow-600 mb-1">No API Key</div>
            <div className="text-2xl font-bold text-yellow-700">{stats.no_api_key}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 mb-6">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-slate-400" />
            <FilterSelect
              value={filters.status}
              onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'success', label: 'Sent' },
                { value: 'failed', label: 'Failed' },
                { value: 'no_api_key', label: 'No API Key' }
              ]}
            />
            <FilterSelect
              value={filters.email_type}
              onChange={(e) => { setFilters(f => ({ ...f, email_type: e.target.value })); setPage(1); }}
              options={[
                { value: '', label: 'All Types' },
                { value: 'verification', label: 'Verification' },
                { value: 'test', label: 'Test' },
                { value: 'tier_notification', label: 'Tier Notification' },
                { value: 'general', label: 'General' }
              ]}
            />
            {(filters.status || filters.email_type) && (
              <button
                onClick={() => { setFilters({ status: '', email_type: '' }); setPage(1); }}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No email logs found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Recipient</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Subject</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Response</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(log.status)}
                        {getStatusBadge(log.status)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getTypeBadge(log.email_type)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {log.to_email}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 max-w-xs truncate">
                      {log.subject}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {log.response_code ? (
                        <span className={`font-mono ${log.response_code >= 200 && log.response_code < 300 ? 'text-green-600' : 'text-red-600'}`}>
                          {log.response_code}
                        </span>
                      ) : log.error_message ? (
                        <span className="text-red-500 text-xs" title={log.error_message}>
                          {log.error_message.substring(0, 30)}...
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {formatDate(log.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-slate-500">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminEmailLogs;
